<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Menu } from 'ant-design-vue';
import { useMenu } from '@/composables/useMenu';
import { modulesConfig } from '@/router/modulesConfig';
import { sideNavSections } from '@/router/sideNav';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { usePermission } from '@/composables/usePermission';

const props = defineProps({
    collapsed: {
        type: Boolean,
        default: false
    }
});

const router = useRouter();
const route = useRoute();
const { getMenuData } = useMenu();
const adminStore = useAdminInfo();
const { canView: canViewScheduleCalendarLeaf } = usePermission(
    'ConsultantScheduleCalendar'
);
const { canView: canViewConsultantManage } = usePermission('Consultants');
const canViewScheduleCalendar = computed(
    () =>
        canViewScheduleCalendarLeaf.value || canViewConsultantManage.value
);

const pathByName = computed(() => {
    const map = new Map();
    getMenuData(modulesConfig).forEach((folder) => {
        const children = Array.isArray(folder.children) ? folder.children : [];
        if (children.length === 0) {
            map.set(folder.name, `/${folder.path}`);
            return;
        }
        children.forEach((child) => {
            const seg = String(child.path || '').replace(/^\/+|\/+$/g, '');
            map.set(child.name, seg ? `/${folder.path}/${seg}` : `/${folder.path}`);
        });
    });
    return map;
});

const sections = computed(() => {
    const paths = pathByName.value;
    return sideNavSections
        .map((section) => {
            const entries = [];
            section.entries.forEach((entry) => {
                if (entry.kind === 'dashboard') {
                    if (adminStore.isConsultantOnly || adminStore.isBookingEngineerOnly) return;
                    entries.push({
                        key: 'dashboard',
                        label: entry.label,
                        icon: entry.icon
                    });
                    return;
                }
                if (entry.kind === 'calendar') {
                    if (!canViewScheduleCalendar.value) return;
                    entries.push({
                        key: entry.path,
                        label: entry.label,
                        icon: entry.icon
                    });
                    return;
                }
                if (entry.kind === 'group') {
                    const children = entry.children
                        .filter((child) => paths.has(child.name))
                        .map((child) => ({
                            key: paths.get(child.name),
                            label: child.label
                        }));
                    if (children.length === 0) return;
                    if (children.length === 1) {
                        entries.push({
                            key: children[0].key,
                            label: children[0].label,
                            icon: entry.icon
                        });
                        return;
                    }
                    entries.push({
                        key: `group:${section.key}:${entry.label}`,
                        label: entry.label,
                        icon: entry.icon,
                        children
                    });
                    return;
                }
                const path = paths.get(entry.name);
                if (!path) return;
                entries.push({
                    key: path,
                    label: entry.label,
                    icon: entry.icon
                });
            });
            return entries.length ? { ...section, entries } : null;
        })
        .filter(Boolean);
});

const selectedKeys = computed(() => {
    const currentPath = route.path;
    if (currentPath === '/dashboard') return ['dashboard'];

    for (const section of sections.value) {
        for (const entry of section.entries) {
            if (entry.children?.length) {
                const hit = entry.children.find(
                    (child) =>
                        currentPath === child.key ||
                        currentPath.startsWith(`${child.key}/`)
                );
                if (hit) return [hit.key];
                continue;
            }
            if (
                entry.key !== 'dashboard' &&
                (currentPath === entry.key ||
                    currentPath.startsWith(`${entry.key}/`))
            ) {
                return [entry.key];
            }
        }
    }
    return [];
});

const openKeysFromRoute = () => {
    const currentPath = route.path;
    for (const section of sections.value) {
        for (const entry of section.entries) {
            if (!entry.children?.length) continue;
            const hit = entry.children.some(
                (child) =>
                    currentPath === child.key ||
                    currentPath.startsWith(`${child.key}/`)
            );
            if (hit) return [entry.key];
        }
    }
    return [];
};

const openKeys = ref([]);
watch(
    () => [route.path, props.collapsed],
    () => {
        openKeys.value = props.collapsed ? [] : openKeysFromRoute();
    },
    { immediate: true }
);
const onOpenChange = (keys) => {
    openKeys.value = keys;
};

const handleMenuClick = ({ key }) => {
    if (String(key).startsWith('group:')) return;
    if (key === 'dashboard') {
        router.push('/dashboard');
        return;
    }
    router.push(key).catch((err) => {
        console.error('SideMenu - 路由跳轉失敗:', err);
    });
};
</script>

<template>
    <div class="side-menu">
        <Menu
            mode="inline"
            :selectedKeys="selectedKeys"
            :openKeys="openKeys"
            :inlineCollapsed="collapsed"
            @openChange="onOpenChange"
            @click="handleMenuClick"
            class="h-full"
        >
            <Menu.ItemGroup
                v-for="section in sections"
                :key="section.key"
                :title="section.title"
            >
                <template v-for="entry in section.entries" :key="entry.key">
                    <Menu.SubMenu v-if="entry.children?.length" :key="entry.key">
                        <template #icon>
                            <component :is="entry.icon" />
                        </template>
                        <template #title>{{ entry.label }}</template>
                        <Menu.Item v-for="child in entry.children" :key="child.key">
                            {{ child.label }}
                        </Menu.Item>
                    </Menu.SubMenu>
                    <Menu.Item v-else :key="entry.key">
                        <template #icon>
                            <component :is="entry.icon" />
                        </template>
                        {{ entry.label }}
                    </Menu.Item>
                </template>
            </Menu.ItemGroup>
        </Menu>
    </div>
</template>

<style scoped>
.side-menu {
    height: calc(100vh - 64px);
    overflow-y: auto;
    margin-top: 64px;
}

.side-menu :deep(.ant-menu) {
    border-right: none;
    height: 100%;
}

.side-menu :deep(.ant-menu-item-group-title) {
    padding: 18px 16px 6px 22px !important;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    color: #9a9184;
    line-height: 1.2;
}

.side-menu :deep(.ant-menu-item-group:first-child .ant-menu-item-group-title) {
    padding-top: 12px !important;
}

.side-menu :deep(.ant-menu-item-group-list) {
    margin: 0;
}

.side-menu :deep(.ant-menu-item) {
    margin: 2px 8px;
    width: calc(100% - 16px);
    border-radius: 8px;
    padding-left: 16px !important;
}

.side-menu :deep(.ant-menu-submenu) {
    margin: 0;
}

.side-menu :deep(.ant-menu-submenu .ant-menu-submenu-title) {
    margin: 2px 8px;
    width: calc(100% - 16px);
    border-radius: 8px;
    padding-left: 16px !important;
}

.side-menu :deep(.ant-menu-submenu .ant-menu-item) {
    padding-left: 40px !important;
    margin-left: 8px !important;
}

.side-menu :deep(.ant-menu-inline-collapsed) .ant-menu-item-group-title {
    display: none;
}

.side-menu :deep(.ant-menu-inline-collapsed) .ant-menu-item,
.side-menu :deep(.ant-menu-inline-collapsed) .ant-menu-submenu-title {
    padding-left: 24px !important;
    margin-left: 0 !important;
}

.side-menu :deep(.ant-menu-inline-collapsed) .ant-menu-item-icon {
    margin-right: 0 !important;
}

.side-menu :deep(.ant-menu-inline-collapsed) .ant-menu-submenu-arrow {
    display: none !important;
}

.side-menu :deep(.ant-menu-item-selected) {
    background-color: rgba(196, 163, 90, 0.18) !important;
    color: var(--gh-brand-active) !important;
    font-weight: 600;
}

.side-menu :deep(.ant-menu-submenu-selected > .ant-menu-submenu-title) {
    color: var(--gh-brand-active) !important;
}

.side-menu :deep(.ant-menu-item-selected .anticon),
.side-menu :deep(.ant-menu-submenu-selected .anticon) {
    color: var(--gh-brand) !important;
}

.side-menu::-webkit-scrollbar {
    display: none;
}

.side-menu {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
